/**
 * Screen 1f — who gets this, and by when.
 *
 * One dialog states the whole thing: several scopes, one deadline, one reminder
 * decision, one `POST /assignments`. Assigning a scope that already has a live
 * assignment for this target moves its deadline rather than adding a second
 * row, which is what the partial unique index enforces — so re-opening this on
 * a course that is already assigned to Operations and pressing Assign is an
 * edit, not a duplicate.
 *
 * ── The count under the button ──────────────────────────────────────────────
 * "Assign to 61 people" is the union of the chosen scopes, resolved here so the
 * number moves as chips are added. `PersonWire` carries a team *name* and not a
 * role id, so a role's members are matched by that name; team names are unique
 * inside an organisation, and the alternative is a request per chip. The
 * authoritative count is the server's, and it comes back in the result.
 *
 * ── The schedule is not edited here ─────────────────────────────────────────
 * `reminderOffsets: null` is sent, always: the assignment inherits the
 * organisation's schedule, and the line under the switch says whose it is. An
 * editor here would be a second place to set one number, and the People page
 * already has the first.
 */

import { useMemo, useState } from 'react';
import type React from 'react';
import type { AssignScope, AssignResultWire, PeopleWire, PersonWire } from '@wire';
import { errorText, teach } from '../api';
import { ds } from '../ds';
import { Footnote, ErrorNote } from './Chrome';
import { Avatar } from './Bits';
import { HAIR, RADIUS, formatDeadline, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

const DAY = 86400;

/** "7 days before · 1 day before · on the day" — the design's own words. */
export function offsetsLabel(offsets: number[]): string {
  return offsets
    .map((d) => (d === 0 ? 'on the day' : d === 1 ? '1 day before' : `${d} days before`))
    .join(' · ');
}

/** "Repeats" and "Expires" — whole months, the server's unit. */
const REPEATS = [
  { value: '', label: 'Once' },
  { value: '6', label: 'Every 6 months' },
  { value: '12', label: 'Every year' },
  { value: '24', label: 'Every 2 years' }
];
const EXPIRES = [
  { value: '', label: 'Never' },
  { value: '6', label: 'After 6 months' },
  { value: '12', label: 'After 1 year' },
  { value: '24', label: 'After 2 years' },
  { value: '36', label: 'After 3 years' }
];

const PRESETS: { label: string; days: number | null }[] = [
  { label: '1 week', days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
  { label: 'None', days: null }
];

/** End of the chosen day, so "due Friday" means all of Friday. */
const endOfDay = (date: Date): number =>
  Math.floor(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59).getTime() / 1000);

/** `<input type="date">` wants the *local* day. `toISOString` would give the
 *  UTC one, which west of Greenwich is tomorrow for every deadline set here. */
function dateInputValue(unix: number | null): string {
  if (unix === null) return '';
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The member ids a set of scopes resolves to, right now.
 *
 * The same arithmetic `reach` always did for the chips being chosen, pulled
 * out so it can also be run over the target's *existing* assignments — which
 * is what makes an honest overlap possible below. `PersonWire` carries a
 * team *name* rather than a role id (see the file header), so a role's
 * members are matched by that name.
 */
function resolveMembers(scopes: AssignScope[], roster: PeopleWire | null): Set<string> {
  if (!roster) return new Set();
  if (scopes.includes('')) return new Set(roster.members.map((m) => m.userId));

  const roleNames = new Set(
    scopes
      .filter((s) => s.startsWith('role:'))
      .map((s) => roster.roles.find((r) => r.id === s.slice('role:'.length))?.name)
      .filter((n): n is string => !!n)
  );
  const chosen = new Set<string>(scopes.filter((s) => s.startsWith('usr_')));

  return new Set(
    roster.members
      .filter((m) => (m.team && roleNames.has(m.team)) || chosen.has(m.userId))
      .map((m) => m.userId)
  );
}

export function AssignDialog({
  courseId,
  bundleId,
  subject,
  onClose,
  onDone
}: {
  courseId?: string;
  bundleId?: string;
  /** What is being assigned, for the dialog's title. */
  subject: string;
  onClose: () => void;
  onDone: (result: AssignResultWire) => void;
}) {
  const { Button, Dialog, Icon, Input, Select, Switch, Tag } = ds();

  const people = useLoad(() => teach.people(), 'Could not load the roster.');
  const current = useLoad(
    () => teach.assignments.list(courseId ? { courseId } : { bundleId }),
    'Could not load who has this already.',
    courseId ?? bundleId ?? ''
  );

  const [scopes, setScopes] = useState<AssignScope[]>([]);
  const [filter, setFilter] = useState('');
  const [dueAt, setDueAt] = useState<number | null>(() => Math.floor(Date.now() / 1000) + 7 * DAY);
  const [remind, setRemind] = useState(true);
  const [repeat, setRepeat] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roster = people.data;
  const reminders = roster?.reminders ?? { remind: true, offsets: [7, 1, 0] };

  const add = (scope: AssignScope) => {
    setScopes((s) => (s.includes(scope) ? s : [...s, scope]));
    setFilter('');
  };
  const remove = (scope: AssignScope) => setScopes((s) => s.filter((x) => x !== scope));

  /** The chip's word for a scope. */
  const labelFor = (scope: AssignScope): string => {
    if (scope === '') return 'Everyone';
    if (scope.startsWith('role:')) {
      const id = scope.slice('role:'.length);
      return roster?.roles.find((r) => r.id === id)?.name ?? 'Team';
    }
    return roster?.members.find((m) => m.userId === scope)?.name ?? 'Someone';
  };

  const reachSet = useMemo(() => resolveMembers(scopes, roster), [roster, scopes]);
  const reach = reachSet.size;

  /**
   * How many of the people about to be assigned already have this.
   *
   * `AssignmentsWire.people` (below, in the footer) is the course's whole
   * distinct roster today — not the overlap with the chips just picked — so
   * "3 already assigned" beside "Assign to 1 person" used to read as a
   * contradiction. This resolves the *current* assignments' scopes the same
   * way and intersects the two sets.
   */
  const alreadySet = useMemo(
    () => resolveMembers((current.data?.assignments ?? []).map((a) => a.scope), roster),
    [roster, current.data]
  );
  const overlap = useMemo(() => {
    let n = 0;
    for (const id of reachSet) if (alreadySet.has(id)) n++;
    return n;
  }, [reachSet, alreadySet]);

  const suggestions = useMemo(() => {
    if (!roster) return { roles: [], members: [] as PersonWire[] };
    const q = filter.trim().toLowerCase();
    const match = (s: string) => s.toLowerCase().includes(q);
    return {
      roles: roster.roles.filter((r) => !q || match(r.name)),
      members: roster.members
        .filter((m) => !q || match(m.name) || (m.handle ? match(m.handle) : false))
        .slice(0, q ? 20 : 8)
    };
  }, [roster, filter]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await teach.assignments.create({
        ...(courseId ? { courseId } : { bundleId: bundleId! }),
        scopes,
        dueAt,
        remind,
        reminderOffsets: null,
        // A repeat needs a deadline to step on from; without one it is sent
        // as once rather than refused.
        repeatMonths: repeat && dueAt !== null ? Number(repeat) : null,
        validMonths: expires ? Number(expires) : null
      });
      onDone(result);
    } catch (err) {
      setError(errorText(err, 'Could not assign it.'));
      setBusy(false);
    }
  };

  /** Revoke one live assignment. Its own busy flag would be a second one next
   *  to the dialog's — this reuses it, the same as `submit` does. */
  const revoke = async (assignmentId: string) => {
    setBusy(true);
    setError(null);
    try {
      await teach.assignments.revoke(assignmentId);
      await current.reload();
    } catch (err) {
      setError(errorText(err, 'Could not remove that assignment.'));
    }
    setBusy(false);
  };

  const assignments = current.data?.assignments ?? [];

  return (
    <Dialog
      open
      position="fixed"
      width={560}
      title={`Assign ${subject}`}
      onClose={busy ? undefined : onClose}
      footer={
        <>
          <span style={{ ...monoPlain, marginRight: 'auto', alignSelf: 'center' }}>
            {overlap > 0 ? `${overlap} already assigned` : ''}
          </span>
          <Button variant="ghost" size="md" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={busy || scopes.length === 0}
            onClick={() => void submit()}
          >
            {`Assign to ${plural(reach, 'person', 'people')}`}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 18 }}>
        <ErrorNote>{error ?? people.error ?? current.error ?? null}</ErrorNote>

        {assignments.length > 0 && (
          <div style={{ display: 'grid', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>
              Already assigned
            </span>
            <div style={{ display: 'grid', gap: 2 }}>
              {assignments.map((a) => (
                <div
                  key={a.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}
                >
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--text-strong)' }}>
                    {a.scopeLabel}
                  </span>
                  <span style={monoPlain}>
                    {a.dueAt === null ? 'No deadline' : `Due ${formatDeadline(a.dueAt)}`}
                  </span>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => void revoke(a.id)}>
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Who</span>

          {scopes.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {scopes.map((s) => (
                <Tag key={s} selected onRemove={() => remove(s)}>
                  {labelFor(s)}
                </Tag>
              ))}
            </div>
          )}

          <Input
            placeholder="Search teams and people"
            icon="Search"
            size="sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />

          <div
            style={{
              maxHeight: 190,
              overflowY: 'auto',
              border: HAIR,
              borderRadius: RADIUS.control,
              padding: 4
            }}
          >
            <Suggestion
              label="Everyone"
              detail={roster ? plural(roster.members.length, 'person', 'people') : ''}
              chosen={scopes.includes('')}
              onPick={() => add('')}
            />
            {suggestions.roles.map((r) => (
              <Suggestion
                key={r.id}
                label={r.name}
                detail={plural(r.members, 'person', 'people')}
                chosen={scopes.includes(`role:${r.id}`)}
                onPick={() => add(`role:${r.id}`)}
              />
            ))}
            {suggestions.members.map((m) => (
              <Suggestion
                key={m.userId}
                label={m.name}
                detail={m.team ?? (m.handle ? `@${m.handle}` : '')}
                avatar={<Avatar name={m.name} handle={m.handle} />}
                chosen={scopes.includes(m.userId as AssignScope)}
                onPick={() => add(m.userId as AssignScope)}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>Deadline</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                variant="secondary"
                size="sm"
                onClick={() =>
                  setDueAt(p.days === null ? null : endOfDay(new Date(Date.now() + p.days * DAY * 1000)))
                }
              >
                {p.label}
              </Button>
            ))}
            <Input
              type="date"
              size="sm"
              style={{ width: 168 }}
              value={dateInputValue(dueAt)}
              onChange={(e) =>
                setDueAt(e.target.value ? endOfDay(new Date(`${e.target.value}T12:00:00`)) : null)
              }
            />
          </div>
          <Footnote>{dueAt ? `Due ${formatDeadline(dueAt)}` : 'No deadline'}</Footnote>
        </div>

        {courseId && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            <Select
              label="Repeats"
              size="sm"
              value={dueAt === null ? '' : repeat}
              disabled={dueAt === null}
              style={{ minWidth: 170 }}
              onChange={(e) => setRepeat(e.target.value)}
              options={REPEATS}
            />
            <Select
              label="Completion expires"
              size="sm"
              value={expires}
              style={{ minWidth: 170 }}
              onChange={(e) => setExpires(e.target.value)}
              options={EXPIRES}
            />
          </div>
        )}

        <div style={{ display: 'grid', gap: 8 }}>
          <Switch
            label="Remind them automatically"
            checked={remind}
            onChange={(next) => setRemind(next)}
          />
          {remind && <Footnote>{offsetsLabel(reminders.offsets)}</Footnote>}
          <Footnote>Managers set the default schedule for everyone in People → Reminders.</Footnote>
          <div style={{ display: 'flex', gap: 7, alignItems: 'center', ...monoPlain }}>
            <Icon name="Bell" size={13} />
            It shows up in their notifications across the Mauvely suite.
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function Suggestion({
  label,
  detail,
  chosen,
  avatar,
  onPick
}: {
  label: string;
  detail: string;
  chosen: boolean;
  avatar?: React.ReactNode;
  onPick: () => void;
}) {
  const { Icon } = ds();
  return (
    <button
      type="button"
      className="mv-row"
      onClick={onPick}
      disabled={chosen}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '7px 9px',
        border: 'none',
        borderRadius: 8,
        background: 'transparent',
        textAlign: 'left'
      }}
    >
      {avatar}
      <span style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>{label}</span>
      <span style={{ ...monoPlain, marginLeft: 'auto' }}>{detail}</span>
      {chosen && <Icon name="Check" size={14} />}
    </button>
  );
}
